import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { m1Users, m2Users, M1User, M2User } from "../../mocks/users";
import { M1CodeTable, M2CodeTable } from "./components/CodeTable";
import ProductsManager from "./components/ProductsManager";
import CompaniesManager from "./components/CompaniesManager";
import EventPopupsManager from "./components/EventPopupsManager";
import KoreanBriefManager from "./components/KoreanBriefManager";
import InquiryReplyManager from "./components/InquiryReplyManager";
import InquiryChatManager from "./components/InquiryChatManager";
import CustomersManager from "./components/CustomersManager";
import AssociationManager from "./components/AssociationManager";
import HeroBannerManager from "./components/HeroBannerManager";
import SupportProgramManager from "./components/SupportProgramManager";
import SuperAdminStaffManager from "./components/SuperAdminStaffManager";
import StaffChangePassword from "./components/StaffChangePassword";
import TechCooperationManager from "./components/TechCooperationManager";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import IntroductionManager from "./components/IntroductionManager";

type Section = "overview" | "products" | "companies" | "popups" | "brief" | "inquiries" | "customers" | "m1-codes" | "association" | "hero-banners" | "support" | "super-staff" | "tech-cooperation" | "introduction";
type Lang = "vi" | "kr";

interface AuthUser {
  type: "m1" | "m2";
  user: M1User | M2User;
}

// ... existing code ...

const AdminPage = () => {
  const navigate = useNavigate();
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [section, setSection] = useState<Section>("overview");
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState<Lang>("vi");
  const [unreadInquiries, setUnreadInquiries] = useState(0);
  const [productCount, setProductCount] = useState<number | null>(null);
  const [companyCount, setCompanyCount] = useState<number | null>(null);

  const fetchCounts = useCallback(async () => {
    const { count: pCount } = await supabase
      .from("san_pham_han_quoc")
      .select("*", { count: "exact", head: true });
    const { count: cCount } = await supabase
      .from("companies")
      .select("*", { count: "exact", head: true });
    setProductCount(pCount ?? 0);
    setCompanyCount(cCount ?? 0);
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  const fetchUnreadCount = useCallback(async () => {
    const { count } = await supabase
      .from("inquiries")
      .select("*", { count: "exact", head: true })
      .eq("status", "new");
    setUnreadInquiries(count ?? 0);
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    // Poll every 30s for new inquiries
    const interval = setInterval(fetchUnreadCount, 30000);
    // Also subscribe realtime
    const channel = supabase
      .channel("admin-inquiry-badge")
      .on("postgres_changes", { event: "*", schema: "public", table: "inquiries" }, () => {
        fetchUnreadCount();
      })
      .subscribe();
    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, [fetchUnreadCount]);

  const t = (vi: string, kr: string) => (lang === "vi" ? vi : kr);

  useEffect(() => {
    const stored = localStorage.getItem("bizwell_auth");
    if (!stored) { navigate("/login"); return; }
    const auth = JSON.parse(stored) as AuthUser;
    if (auth.type !== "m1") { navigate("/login"); return; }
    setAuthUser(auth);
    setLoading(false);
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("bizwell_auth");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <i className="ri-loader-4-line text-3xl text-[#004A99] animate-spin block mb-3" />
          <p className="text-sm text-gray-500">{t("Đang tải...", "로딩 중...")}</p>
        </div>
      </div>
    );
  }

  if (!authUser) return null;
  const currentUser = authUser.user as M1User;
  const isSuperAdmin = currentUser.code === "M1-11-000";

  const navItems: { id: Section; label: string; labelKr: string; icon: string; badge?: number; superAdminOnly?: boolean }[] = [
    { id: "hero-banners", label: "Hero Banner", labelKr: "Hero 배너", icon: "ri-image-2-line" },
    { id: "overview", label: "Tổng quan", labelKr: "대시보드", icon: "ri-dashboard-line" },
    { id: "products", label: "Sản Phẩm", labelKr: "제품 관리", icon: "ri-box-3-line" },
    { id: "companies", label: "Doanh Nghiệp", labelKr: "기업 관리", icon: "ri-building-2-line" },
    { id: "popups", label: "Popup Sự Kiện", labelKr: "이벤트 팝업", icon: "ri-notification-3-line" },
    { id: "tech-cooperation", label: "Hợp tác kỹ thuật", labelKr: "기술협력", icon: "ri-team-line" },
    { id: "brief", label: "Korean Brief", labelKr: "Korean Brief", icon: "ri-newspaper-line" },
    { id: "inquiries", label: "Yêu Cầu Tư Vấn", labelKr: "상담 요청", icon: "ri-mail-line", badge: unreadInquiries },
    { id: "association", label: "Hoạt Động Hiệp Hội", labelKr: "협회 활동", icon: "ri-community-line" },
    { id: "support", label: "Chương Trình Hỗ Trợ", labelKr: "지원 프로그램", icon: "ri-exchange-2-line" },
    { id: "customers", label: "Khách Hàng", labelKr: "고객 정보", icon: "ri-user-2-line", badge: m2Users.filter(u => u.status === "pending").length },
    { id: "m1-codes", label: "Nhân Viên (M1)", labelKr: "직원 관리 (M1)", icon: "ri-shield-user-line" },
    { id: "super-staff", label: "Quản Lý Nhân Viên", labelKr: "직원 관리 (Super)", icon: "ri-shield-star-line", superAdminOnly: true },
    { id: "introduction", label: "Giới thiệu", labelKr: "소개", icon: "ri-hand-heart-line" },
  ];

  const visibleNavItems = navItems.filter(item => !item.superAdminOnly || isSuperAdmin);

  const stats = [
    { label: t("Sản phẩm đã đăng", "등록된 제품"), value: productCount !== null ? productCount.toLocaleString() : "—", icon: "ri-box-3-line", color: "bg-emerald-600", note: t("Từ Supabase", "Supabase") },
    { label: t("Doanh nghiệp đã đăng", "등록된 기업"), value: companyCount !== null ? companyCount.toLocaleString() : "—", icon: "ri-building-2-line", color: "bg-[#004A99]", note: t("Từ Supabase", "Supabase") },
    { label: t("Khách hàng M2", "고객 수 M2"), value: m2Users.length, icon: "ri-user-2-line", color: "bg-[#00AEEF]", note: `${m2Users.filter(u => u.status === "pending").length} ${t("chờ duyệt", "대기 중")}` },
    { label: t("Nhân viên M1", "직원 수 M1"), value: m1Users.length, icon: "ri-shield-user-line", color: "bg-amber-500", note: `${m1Users.filter(u => u.status === "active").length} ${t("hoạt động", "활동 중")}` },
  ];

  const exportToCSV = (headers: string[], rows: string[][], filename: string) => {
    const bom = "\uFEFF";
    const csvContent = bom + [headers, ...rows].map((row) => row.map((cell) => `"${cell.replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportM2ToExcel = () => {
    const headers = ["M-Code", "Họ tên", "Điện thoại", "Mã đăng nhập", "Email", "Công ty", "Ngành nghề", "Thành phố", "Ngày đăng ký", "Premium", "Trạng thái"];
    const rows = m2Users.map((u) => [u.code, u.name, u.phone, u.loginKey, u.email, u.company, u.industry, u.city, u.registeredAt, u.premium ? "Premium" : "Free", u.status === "active" ? "Hoạt động" : u.status === "pending" ? "Chờ duyệt" : "Vô hiệu"]);
    exportToCSV(headers, rows, "Bizwell_M2_KhachHang");
  };

  const exportM1ToExcel = () => {
    // Export is handled inside M1CodeTable which uses Supabase data
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      {/* Top header */}
      <header className="bg-[#004A99] text-white px-6 py-3 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <div className="w-7 h-7 bg-white rounded-md flex items-center justify-center">
              <span className="text-[#004A99] font-black text-xs">BW</span>
            </div>
            <span className="font-bold text-sm">Bizwell Admin</span>
          </Link>
          <span className="text-blue-300 text-xs">|</span>
          {isSuperAdmin && (
            <span className="text-amber-300 text-xs font-bold flex items-center gap-1">
              <i className="ri-shield-star-line" />
              Super Admin
            </span>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex bg-white/10 rounded-lg p-0.5 text-xs font-semibold">
            {(["vi", "kr"] as const).map((l) => (
              <button key={l} onClick={() => setLang(l)} className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer whitespace-nowrap font-bold ${lang === l ? "bg-white text-[#004A99]" : "text-white/70 hover:text-white"}`}>
                {l === "vi" ? "🇻🇳 VI" : "🇰🇷 KR"}
              </button>
            ))}
          </div>
          <div className="text-right hidden sm:block">
            <p className="text-xs font-semibold">{currentUser.name}</p>
            <p className="text-[10px] text-blue-300 font-mono">{currentUser.code}</p>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 text-white text-xs rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-logout-box-r-line" />
            {t("Đăng xuất", "로그아웃")}
          </button>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside className="w-56 bg-white border-r border-gray-200 flex-shrink-0 py-6 flex flex-col">
          <div className="px-4 pb-4 border-b border-gray-100">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-2 ${isSuperAdmin ? "bg-amber-100" : "bg-[#004A99]/10"}`}>
              <i className={`${isSuperAdmin ? "ri-shield-star-line text-amber-600" : "ri-shield-user-line text-[#004A99]"} text-lg`} />
            </div>
            <p className="font-bold text-gray-800 text-sm">{currentUser.name}</p>
            <p className="text-[10px] text-gray-400 font-mono">{currentUser.code}</p>
            <span className={`inline-block mt-1.5 text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${isSuperAdmin ? "bg-amber-500 text-white" : "bg-[#004A99] text-white"}`}>
              {isSuperAdmin ? "Super Admin" : currentUser.levelLabel}
            </span>
          </div>

          <nav className="px-3 pt-4 space-y-1 flex-1">
            {isSuperAdmin && (
              <div className="mb-2">
                <p className="text-[10px] text-amber-600 font-bold uppercase tracking-wider px-3 mb-1">Super Admin</p>
                <button
                  onClick={() => setSection("super-staff")}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors cursor-pointer text-left ${section === "super-staff" ? "bg-amber-500 text-white" : "text-amber-700 hover:bg-amber-50"}`}
                >
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-star-line text-sm" />
                  </div>
                  <span className="flex-1 whitespace-nowrap">Quản Lý Nhân Viên</span>
                </button>
                <div className="border-b border-gray-100 mt-2 mb-2" />
              </div>
            )}
            {visibleNavItems.filter(i => !i.superAdminOnly).map((item) => (
              <button
                key={item.id}
                onClick={() => setSection(item.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors cursor-pointer text-left ${section === item.id ? "bg-[#004A99] text-white" : "text-gray-600 hover:bg-gray-100 hover:text-[#004A99]"}`}
              >
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-sm`} />
                </div>
                <span className="flex-1 whitespace-nowrap">{lang === "vi" ? item.label : item.labelKr}</span>
                {item.badge ? (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold whitespace-nowrap ${section === item.id ? "bg-white/20 text-white" : "bg-amber-100 text-amber-700"}`}>
                    {item.badge}
                  </span>
                ) : null}
              </button>
            ))}
          </nav>

          {/* Change password for staff M1-12 */}
          {!isSuperAdmin && (
            <div className="px-4 pt-3 border-t border-gray-100">
              <StaffChangePassword
                staffCode={currentUser.code}
                staffName={currentUser.name}
              />
            </div>
          )}

          <div className="px-4 pt-4 border-t border-gray-100 mt-4 space-y-1">
            <p className="text-[10px] text-gray-400 font-semibold uppercase tracking-wider mb-2">{t("Xuất dữ liệu", "데이터 내보내기")}</p>
            <button onClick={exportM1ToExcel} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-green-700 hover:bg-green-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-file-excel-2-line text-green-600" />
              {t("Xuất M1 (.csv)", "M1 내보내기 (.csv)")}
            </button>
            <button onClick={exportM2ToExcel} className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-green-700 hover:bg-green-50 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-file-excel-2-line text-green-600" />
              {t("Xuất M2 (.csv)", "M2 내보내기 (.csv)")}
            </button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6 min-w-0 overflow-auto">

          {/* ── SUPER ADMIN STAFF ── */}
          {section === "super-staff" && isSuperAdmin && <SuperAdminStaffManager />}

          {/* ── OVERVIEW ── */}
          {section === "overview" && (
            <div>
              <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">{t("Tổng Quan Hệ Thống", "시스템 개요")}</h1>
                <p className="text-sm text-gray-500 mt-1">
                  {t("Chào mừng,", "환영합니다,")} <strong>{currentUser.name}</strong> · {isSuperAdmin ? "Super Admin" : currentUser.levelLabel}
                </p>
              </div>

              {/* Analytics Dashboard */}
              <div className="mb-8">
                <AnalyticsDashboard lang={lang} />
              </div>

              {isSuperAdmin && (
                <div className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-2xl p-5 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center">
                      <i className="ri-shield-star-line text-white text-lg" />
                    </div>
                    <div>
                      <p className="font-bold text-amber-800">Tài khoản Super Admin (M1-11-000)</p>
                      <p className="text-xs text-amber-600">Bạn có quyền truy cập đầy đủ, bao gồm quản lý nhân viên và xem mật khẩu.</p>
                    </div>
                    <button onClick={() => setSection("super-staff")} className="ml-auto px-4 py-2 bg-amber-500 text-white text-xs font-semibold rounded-xl cursor-pointer whitespace-nowrap hover:bg-amber-600 transition-colors">
                      Quản lý nhân viên →
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {stats.map((s) => (
                  <button
                    key={s.label}
                    onClick={() => {
                      if (s.icon === "ri-box-3-line") setSection("products");
                      else if (s.icon === "ri-building-2-line") setSection("companies");
                      else if (s.icon === "ri-user-2-line") setSection("customers");
                      else if (s.icon === "ri-shield-user-line") setSection("m1-codes");
                    }}
                    className="bg-white rounded-2xl border border-gray-200 p-5 hover:border-[#004A99]/30 transition-colors cursor-pointer text-left"
                  >
                    <div className={`w-10 h-10 ${s.color} rounded-xl flex items-center justify-center mb-3`}>
                      <i className={`${s.icon} text-white text-lg`} />
                    </div>
                    <p className="text-2xl font-black text-gray-800">{s.value}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
                    <p className="text-[10px] text-gray-300 mt-1">{s.note}</p>
                  </button>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                <button onClick={() => setSection("products")} className="bg-white rounded-2xl border border-gray-200 p-6 hover:border-[#004A99]/30 transition-all cursor-pointer text-left group">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-emerald-200 transition-colors">
                    <i className="ri-box-3-line text-emerald-700 text-xl" />
                  </div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1">{t("Quản lý Sản Phẩm", "제품 관리")}</h3>
                  <p className="text-xs text-gray-500">{t("Thêm, sửa, xóa sản phẩm Hàn Quốc trực tiếp", "한국 제품 직접 추가·수정·삭제")}</p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-[#004A99] font-semibold">{t("Vào quản lý", "관리로 이동")} <i className="ri-arrow-right-line" /></div>
                </button>
                <button onClick={() => setSection("companies")} className="bg-white rounded-2xl border border-gray-200 p-6 hover:border-[#004A99]/30 transition-all cursor-pointer text-left group">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
                    <i className="ri-building-2-line text-[#004A99] text-xl" />
                  </div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1">{t("Quản lý Doanh Nghiệp", "기업 관리")}</h3>
                  <p className="text-xs text-gray-500">{t("Cập nhật thông tin, logo doanh nghiệp Hàn Quốc", "한국 기업 정보·로고 업데이트")}</p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-[#004A99] font-semibold">{t("Vào quản lý", "관리로 이동")} <i className="ri-arrow-right-line" /></div>
                </button>
                <button onClick={() => setSection("inquiries")} className="bg-white rounded-2xl border border-gray-200 p-6 hover:border-[#004A99]/30 transition-all cursor-pointer text-left group">
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-sky-200 transition-colors">
                    <i className="ri-mail-line text-sky-600 text-xl" />
                  </div>
                  <h3 className="font-bold text-gray-800 text-sm mb-1">{t("Yêu Cầu Tư Vấn", "상담 요청")}</h3>
                  <p className="text-xs text-gray-500">{t("Xem và phản hồi yêu cầu tư vấn từ khách hàng", "고객 상담 요청 확인 및 답변")}</p>
                  <div className="mt-3 flex items-center gap-1 text-xs text-[#004A99] font-semibold">{t("Xem yêu cầu", "요청 보기")} <i className="ri-arrow-right-line" /></div>
                </button>
              </div>

              <div className="bg-[#EDF2FB] border border-[#004A99]/20 rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-3">
                  <i className="ri-information-line text-[#004A99] text-lg" />
                  <h4 className="font-bold text-[#004A99] text-sm">{t("Hướng dẫn sử dụng Admin", "관리자 이용 안내")}</h4>
                </div>
                <ul className="text-xs text-[#004A99]/80 space-y-1.5 list-disc pl-4">
                  <li>{t("Sản phẩm và doanh nghiệp được lưu trong Supabase – thay đổi sẽ phản ánh ngay lên website", "제품 및 기업 정보는 Supabase에 저장되며 변경 사항이 즉시 웹사이트에 반영됩니다")}</li>
                  <li>{t("Chuyển ngôn ngữ VI/KR bằng nút ở góc trên cùng bên phải", "오른쪽 상단의 버튼으로 VI/KR 언어를 전환하세요")}</li>
                  {isSuperAdmin && <li className="text-amber-700">{t("Super Admin: Vào 'Quản Lý Nhân Viên' để thêm nhân viên mới và xem mật khẩu của họ", "Super Admin: '직원 관리'에서 새 직원 추가 및 비밀번호 확인")}</li>}
                </ul>
              </div>
            </div>
          )}

          {section === "products" && <ProductsManager lang={lang} />}
          {section === "companies" && <CompaniesManager lang={lang} />}
          {section === "popups" && <EventPopupsManager lang={lang} />}
          {section === "brief" && <KoreanBriefManager lang={lang} />}
          {section === "inquiries" && <InquiryChatManager lang={lang} />}
          {section === "hero-banners" && <HeroBannerManager lang={lang} />}
          {section === "association" && <AssociationManager lang={lang} />}
          {section === "tech-cooperation" && <TechCooperationManager lang={lang} />}
          {section === "support" && <SupportProgramManager lang={lang} />}
          {section === "customers" && <CustomersManager lang={lang} />}
          {section === "introduction" && <IntroductionManager lang={lang} />}

          {section === "m1-codes" && (
            <div>
              <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">{t("Quản Lý Nhân Viên (M1)", "직원 관리 (M1)")}</h1>
                <p className="text-sm text-gray-500 mt-1">{t("Xem thông tin nhân viên nội bộ Hàn Quốc và Việt Nam", "한국 및 베트남 내부 직원 정보 보기")}</p>
              </div>
              <M1CodeTable data={m1Users} onExport={exportM1ToExcel} />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminPage;
